import { ColumnDef } from "@tanstack/react-table";
import { StructureTypeIcon } from "@/components/StructureTypeIcon";
import { LinkColumn } from "@/components/tableColumns/LinkColumn";
import { ActionsMenuColumn } from "@/components/tableColumns/ActionsMenuColumn";
import { BooleanColumn } from "@/components/tableColumns/BooleanColumn";
import { AppLink } from "@/components/AppLink";
import { NetworkTypeBadge } from "@/components/badges/NetworkTypeBadge";
import { GroupBadgeList } from "@/components/badges/GroupBadge";
import { StructureNameCell } from "@/components/tableColumns/StructureNameCell";
import { ModalStore } from "@/stores/ui/ModalStore";

type ColumnsDeps = {
	modalStore: ModalStore;
	canManageIAndI: boolean;
};

export const getColumns = ({ modalStore, canManageIAndI }: ColumnsDeps): ColumnDef<Dryp.Structure>[] => [
	{
		accessorKey: "id",
		header: "Id",
	},
	LinkColumn({
		accessorKey: "name",
		header: "Name",
		className: "font-medium tracking-tight",
		linkText: row => <StructureNameCell name={row.name} isActive={row.isActive} />,
		link: row => ({
			to: "Dashboard.Structures.Structure",
			params: { id: row.id },
			query: {},
		}),
		tooltipText: row => `Go to ${row.name}`,
	}),
	{
		accessorKey: "type.name",
		id: "type.name",
		enableSorting: false,
		header: "Type",
		cell: ({ row }) => {
			const { id } = row.original.type;
			return (
				<div className="inline-flex">
					<StructureTypeIcon typeId={id} showLabel />
				</div>
			);
		},
	},
	{
		accessorKey: "group",
		id: "group",
		enableSorting: false,
		header: "Groups",
		cell: ({ row }) => {
			const groups = row.original.groups ?? [];
			return <GroupBadgeList groups={groups} selectedIds={new Set(groups.map(({ id }) => id))} maxVisible={2} />;
		},
		meta: {
			exportFormat: (_, row) => row?.groups?.map(g => g.name).join(", ") || "",
		},
	},
	{
		accessorKey: "networkType",
		accessorFn: row => row.networkType ?? "—",
		enableSorting: false,
		header: "Network Type",
		cell: ({ row }) => {
			const networkType = row.original?.networkType;
			if (!networkType) return "-";
			return <NetworkTypeBadge type={networkType} showLabel />;
		},
	},
	BooleanColumn<Dryp.Structure>({
		accessorKey: "hasBlockage",
		header: "Has Blockage",
		align: "left",
		meta: { headerClassName: "text-center" },
	}),
	BooleanColumn<Dryp.Structure>({
		accessorKey: "hasIAndI",
		header: "Has I&I",
		align: "left",
		meta: { headerClassName: "text-center" },
	}),
	BooleanColumn<Dryp.Structure>({
		accessorKey: "hasIAndISensors",
		header: "Has I&I sensors",
		align: "left",
		meta: { headerClassName: "text-center" },
	}),
	ActionsMenuColumn({
		actions: [
			{
				label: "See on map",
				renderMenuItem: row => {
					const { id, coordinate } = row.original;
					const { latitude, longitude } = coordinate;
					const zoom = 19;

					return (
						<AppLink
							to="Dashboard.Map"
							params={{}}
							query={{ lat: latitude, lng: longitude, zoom, structures: id }}
							className="w-full"
						>
							See on map
						</AppLink>
					);
				},
			},
			{
				label: "Edit network type",
				onSelect: (row, table) => {
					modalStore.open("editNetworkType", {
						structureId: row.original.id,
						onSuccess: () => table.options.meta?.refresh?.(),
						structureNetworkType: row.original.networkType,
					});
				},
			},
			{
				label: "Enable I&I",
				onHandleHidden: row => !canManageIAndI || row.original.hasIAndISensors,
				onSelect: (row, table) => {
					modalStore.open("enableIAndI", {
						structureId: row.original.id,
						structureName: row.original.name,
						onSuccess: () => table.options.meta?.refresh?.(),
					});
				},
			},
			{
				label: "Disable I&I",
				onHandleHidden: row => !canManageIAndI || !row.original.hasIAndISensors,
				onSelect: (row, table) => {
					modalStore.open("disableIAndI", {
						structureId: row.original.id,
						structureName: row.original.name,
						onSuccess: () => table.options.meta?.refresh?.(),
					});
				},
			},
		],
		header: "",
		className: "w-10",
		meta: {
			enableExport: false,
		},
	}),
];
